import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/joy';
import { motion } from 'framer-motion';
import FunctionsIcon from '@mui/icons-material/Functions';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const FunctionsEmptyState = ({ onAddNew }) => {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 4,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Stack alignItems="center" spacing={3}>
          {/* Icon */}
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.vars.palette.primary[100]} 0%, ${theme.vars.palette.primary[200]} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'sm',
            }}
          >
            <FunctionsIcon
              sx={{
                fontSize: 48,
                color: 'primary.500',
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            level="h3"
            sx={{
              textAlign: 'center',
              color: 'text.primary',
            }}
          >
            No functions yet
          </Typography>

          {/* Description */}
          <Typography
            level="body-md"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              maxWidth: 400,
            }}
          >
            Create custom Python functions to automate workflows, process task results, and integrate with external systems.
          </Typography>

          {/* Features list */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              mt: 1,
              p: 2,
              bgcolor: 'background.level1',
              borderRadius: 'lg',
            }}
          >
            <FeatureItem
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 20, color: 'success.500' }} />}
              title="Post-Complete"
              description="Run after tasks finish"
            />
            <FeatureItem
              icon={<ScheduleIcon sx={{ fontSize: 20, color: 'primary.500' }} />}
              title="Scheduled"
              description="Run on intervals"
            />
          </Stack>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <Button
              size="lg"
              startDecorator={<AddRoundedIcon />}
              onClick={onAddNew}
              sx={{ mt: 2 }}
            >
              Create your first function
            </Button>
          </motion.div>
        </Stack>
      </motion.div>
    </Box>
  );
};

const FeatureItem = ({ icon, title, description }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{
      px: 2,
      py: 1.5,
      minWidth: 160,
    }}
  >
    {icon}
    <Stack spacing={0}>
      <Typography
        level="title-sm"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
        }}
      >
        {title}
      </Typography>
      <Typography
        level="body-xs"
        sx={{
          color: 'text.tertiary',
        }}
      >
        {description}
      </Typography>
    </Stack>
  </Stack>
);

export default FunctionsEmptyState;
